import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import type { Scope } from '@shiocast/contracts';
import { setUnauthenticatedHandler } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useStationsStore } from '@/stores/stations';
import { NAVIGATION } from './navigation';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, chrome: false },
  },
  {
    path: '/',
    name: 'on-air',
    component: () => import('@/views/OnAirView.vue'),
    meta: { scope: 'playout:view' },
  },
  {
    // Everyone who can sign in may reach their own account, so no scope: a presenter
    // with nothing but live:broadcast still has a second factor to manage.
    path: '/account/security',
    name: 'account-security',
    component: () => import('@/views/AccountSecurityView.vue'),
  },
  {
    // Also no scope. Everybody may see which organisation they are in and what it is
    // on; what they may change inside it is decided by their role there.
    path: '/account/organisation',
    name: 'organisation',
    component: () => import('@/views/OrganisationView.vue'),
  },
  {
    path: '/operations/health',
    name: 'health',
    component: () => import('@/views/HealthView.vue'),
    meta: { scope: 'station:view' },
  },
  {
    path: '/on-air/timeline',
    name: 'timeline',
    component: () => import('@/views/TimelineView.vue'),
    meta: { scope: 'playout:view' },
  },
  {
    path: '/on-air/requests',
    name: 'requests',
    component: () => import('@/views/RequestsView.vue'),
    meta: { scope: 'playout:view' },
  },
  {
    path: '/programming/schedule',
    name: 'schedule',
    component: () => import('@/views/ScheduleView.vue'),
    meta: { scope: 'schedule:view' },
  },
  {
    path: '/programming/shows',
    name: 'shows',
    component: () => import('@/views/ShowsView.vue'),
    meta: { scope: 'schedule:view' },
  },
  {
    path: '/programming/episodes',
    name: 'episodes',
    component: () => import('@/views/EpisodesView.vue'),
    meta: { scope: 'schedule:view' },
  },
  {
    path: '/programming/playlists',
    name: 'playlists',
    component: () => import('@/views/PlaylistsView.vue'),
    meta: { scope: 'schedule:view' },
  },
  {
    path: '/programming/playlist-studio',
    name: 'playlist-studio',
    component: () => import('@/views/PlaylistStudioView.vue'),
    meta: { scope: 'schedule:view' },
  },
  {
    path: '/media/library',
    name: 'media',
    component: () => import('@/views/MediaView.vue'),
    meta: { scope: 'media:view' },
  },
  {
    path: '/operations/alerts',
    name: 'alerts',
    component: () => import('@/views/AlertsView.vue'),
    meta: { scope: 'station:view' },
  },
  {
    path: '/operations/audit',
    name: 'audit',
    component: () => import('@/views/AuditView.vue'),
    meta: { scope: 'audit:view' },
  },
  {
    path: '/station/domains',
    name: 'domains',
    component: () => import('@/views/DomainsView.vue'),
    meta: { scope: 'station:view' },
  },
  {
    path: '/station/streams',
    name: 'streams',
    component: () => import('@/views/StreamsView.vue'),
    meta: { scope: 'station:view' },
  },
  {
    path: '/on-air/live',
    name: 'live-studio',
    component: () => import('@/views/LiveStudioView.vue'),
    meta: { scope: 'live:manage_streamers' },
  },
  {
    path: '/programming/clocks',
    name: 'clocks',
    component: () => import('@/views/ClocksView.vue'),
    meta: { scope: 'schedule:view' },
  },
  {
    path: '/media/uploads',
    name: 'uploads',
    component: () => import('@/views/UploadsView.vue'),
    meta: { scope: 'media:manage' },
  },
  {
    path: '/media/recordings',
    name: 'recordings',
    component: () => import('@/views/RecordingsView.vue'),
    meta: { scope: 'media:view' },
  },
  {
    path: '/media/archive',
    name: 'archive',
    component: () => import('@/views/ArchiveView.vue'),
    meta: { scope: 'media:view' },
  },
  {
    path: '/podcasts/shows',
    name: 'podcast-feeds',
    component: () => import('@/views/PodcastFeedsView.vue'),
    meta: { scope: 'podcast:view' },
  },
  {
    path: '/podcasts/distribution',
    name: 'distribution',
    component: () => import('@/views/DistributionView.vue'),
    meta: { scope: 'podcast:manage' },
  },
  {
    path: '/podcasts/analytics',
    name: 'podcast-analytics',
    component: () => import('@/views/PodcastAnalyticsView.vue'),
    meta: { scope: 'analytics:view' },
  },
  {
    path: '/audience/live',
    name: 'live-listeners',
    component: () => import('@/views/LiveListenersView.vue'),
    meta: { scope: 'analytics:view' },
  },
  {
    path: '/audience/analytics',
    name: 'audience-analytics',
    component: () => import('@/views/AudienceAnalyticsView.vue'),
    meta: { scope: 'analytics:view' },
  },
  {
    path: '/audience/stream-health',
    name: 'stream-health',
    component: () => import('@/views/StreamHealthView.vue'),
    meta: { scope: 'station:view' },
  },
  {
    path: '/operations/jobs',
    name: 'jobs',
    component: () => import('@/views/JobsView.vue'),
    meta: { scope: 'station:view' },
  },
  {
    path: '/station/streamers',
    name: 'streamers',
    component: () => import('@/views/StreamersView.vue'),
    meta: { scope: 'live:manage_streamers' },
  },
  {
    path: '/station/fallback',
    name: 'fallback',
    component: () => import('@/views/FallbackView.vue'),
    meta: { scope: 'station:configure' },
  },
  {
    path: '/station/users',
    name: 'users',
    component: () => import('@/views/UsersView.vue'),
    meta: { scope: 'users:manage' },
  },
  {
    path: '/station/notifications',
    name: 'notifications',
    component: () => import('@/views/NotificationsView.vue'),
    meta: { scope: 'station:view' },
  },
  {
    path: '/station/api-keys',
    name: 'api-keys',
    component: () => import('@/views/ApiKeysView.vue'),
    meta: { scope: 'station:view' },
  },
  {
    path: '/station/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { scope: 'settings:manage' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
];

if (__SHIOCAST_SHOWCASE__) {
  routes.push({
    path: '/dev/design-system',
    name: 'design-system',
    component: () => import('@/views/DesignSystemView.vue'),
    // Reachable without signing in, because the design language has to be reviewable
    // before there is a seeded account to sign in with. This route does not exist at
    // all in a production build unless it is deliberately enabled.
    meta: { public: true, chrome: false },
  });
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

/** The first page in the rail this account may open, or null when there is none. */
function firstAllowedPath(can: (scope: Scope) => boolean): string | null {
  for (const group of NAVIGATION) {
    for (const item of group.items) {
      if (!item.scope || can(item.scope)) return item.to;
    }
  }
  return null;
}

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // One restore attempt per page load, before the first guarded navigation resolves.
  if (auth.initialising) await auth.restore();

  if (to.meta.public) return true;

  if (!auth.isAuthenticated) {
    return { name: 'login', query: to.fullPath === '/' ? {} : { next: to.fullPath } };
  }

  // Before the first screen asks the API for anything. A request that went out before
  // the station was settled would be answered for a different one, or for none.
  const stations = useStationsStore();
  if (!stations.loaded) {
    try {
      await stations.load();
    } catch {
      // An account with no station can still reach its own account pages; the screens
      // that need one say so themselves.
    }
  }

  // The API enforces this too. The guard exists so an operator is not shown a page
  // that will only produce a permission error, not as the authorisation itself.
  const scope = to.meta.scope as Scope | undefined;
  if (scope && !auth.can(scope)) {
    // Somewhere this account can actually open, never a fixed page it may not: a
    // viewer without playout:view bounced to the dashboard would bounce forever.
    const fallback = firstAllowedPath(auth.can);
    if (fallback && fallback !== to.path) return { path: fallback };
    if (!fallback) {
      auth.clear();
      return { name: 'login' };
    }
  }

  return true;
});

// A 401 the refresh could not repair: the session is gone. Sending the operator to the
// sign-in page with the current address keeps their place for after they sign in. Wired
// here rather than in the auth store because the store must not import the router.
setUnauthenticatedHandler(() => {
  useAuthStore().clear();
  const current = router.currentRoute.value;
  if (current.name === 'login' || current.meta.public) return;
  void router.replace({
    name: 'login',
    query: current.fullPath === '/' ? {} : { next: current.fullPath },
  });
});
